export class TimeoutError extends Error {
  constructor(ms: number) {
    super(`Timed out after ${ms} ms`);
    this.name = "TimeoutError";
  }
}

/**
 * Runs `task` with an AbortSignal that fires after `ms`. Whether or not the
 * task honours the signal, the returned promise rejects with a TimeoutError
 * once the deadline passes, so a stalled request can never hang its caller.
 */
export function withTimeout<T>(
  task: (signal: AbortSignal) => Promise<T>,
  ms: number,
): Promise<T> {
  const controller = new AbortController();

  return new Promise<T>((resolve, reject) => {
    const deadline = setTimeout(() => {
      const error = new TimeoutError(ms);
      controller.abort(error);
      reject(error);
    }, ms);

    // Clear the deadline before settling so no timer outlives the result.
    Promise.resolve()
      .then(() => task(controller.signal))
      .then(
        (value) => {
          clearTimeout(deadline);
          resolve(value);
        },
        (error: unknown) => {
          clearTimeout(deadline);
          reject(error);
        },
      );
  });
}
